import {
  BINARY_DATA_TYPE,
  ENUM_COLUMN_TYPE,
  FILTER_KIND_INPUT,
  FILTER_KIND_OUTPUT,
  FILTER_MAP_KEYS,
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
} from "../types/constants";
import { getFilterTypeFromResultColumn } from "../types/filterTypes";
import {
  createNumberFormatter,
  filterValueMultiplier,
  subTypeValueAffixes,
} from "./numberUtils";
import {
  filterDisplayColumn,
  filterTargetColumn,
  isGroupingColumn,
} from "./rangeGroupingUtils";
import * as labels from "config.labels";

/**
 * Returns the names of the filter kinds. Without grouping the output filter is
 * simply "Filter"; with grouping the two kinds say what they filter.
 */
export const filterKindLabels = (translateLabels, groupingActive) => ({
  [FILTER_KIND_INPUT]: translateLabels(labels.REPORTS_V3_FILTER_KIND_ROWS, {
    default: "Filter rows (before grouping)",
  }),
  [FILTER_KIND_OUTPUT]: groupingActive
    ? translateLabels(labels.REPORTS_V3_FILTER_KIND_GROUPS, {
        default: "Filter groups (after grouping)",
      })
    : translateLabels(labels.REPORTS_V3_FILTER_KIND_PLAIN, {
        default: "Filter",
      }),
});

const BLANK_VALUE_LABEL = "<blank>";
const MAX_CHIP_VALUES = 2;
const MAX_TOOLTIP_VALUES = 20;

/**
 * Returns the label of a value filter chip: the chosen values themselves, up to
 * two, or the first and a count of the rest ("Construction +2"), with the
 * values listed in the tooltip.
 */
export const selectedValuesChipLabel = (values, getValueLabel) => {
  const names = Array.from(values ?? []).map((value) =>
    value === null || value === undefined || value === ""
      ? BLANK_VALUE_LABEL
      : String(getValueLabel?.(value) ?? value)
  );
  const label =
    names.length <= MAX_CHIP_VALUES
      ? names.join(", ")
      : `${names[0]} +${names.length - 1}`;
  const listed = names.slice(0, MAX_TOOLTIP_VALUES).join(", ");
  const tooltip =
    names.length > MAX_TOOLTIP_VALUES
      ? `${listed} +${names.length - MAX_TOOLTIP_VALUES}`
      : listed;
  return { label, tooltip: names.length > 1 ? tooltip : undefined };
};

/** Returns the filter kinds offered for a column, input first. */
export function filterKindsForColumn(
  column,
  { groupingActive, extraAggregateColumns, hasInputFilter }
) {
  if (!groupingActive) {
    return hasInputFilter
      ? [FILTER_KIND_INPUT, FILTER_KIND_OUTPUT]
      : [FILTER_KIND_OUTPUT];
  }
  const isExtraAggregate = (extraAggregateColumns ?? []).some(
    (e) => e.column === column && e.sourceColumn !== column
  );
  return isExtraAggregate
    ? [FILTER_KIND_OUTPUT]
    : [FILTER_KIND_INPUT, FILTER_KIND_OUTPUT];
}

function extraAggregateColumnNames(spec) {
  const names = new Set();
  (spec?.group ?? []).forEach((group) => {
    (group?.aggregate ?? []).forEach((entry) => {
      const source = entry?.expression?.sourceColumn;
      if (source && source !== entry.column) names.add(entry.column);
    });
  });
  return names;
}

/** Returns a copy of a spec reduced to its ungrouped, unfiltered input columns. */
export function buildInputFilterSummarySpec(spec) {
  const extraNames = extraAggregateColumnNames(spec);
  return {
    ...spec,
    group: null,
    filters: [],
    preAggregationFilters: null,
    summaryRow: null,
    order: [],
    outputColumns: (spec?.outputColumns ?? []).filter(
      (col) => !isGroupingColumn(col?.column) && !extraNames.has(col?.column)
    ),
  };
}

/** Returns the report data for summarising input columns of the selected output report, or null. */
export function buildInputFilterSummaryReport(reportData, selectedOutputIndex) {
  if (!reportData) return null;
  if (!reportData.reports) {
    return {
      ...reportData,
      specification: buildInputFilterSummarySpec(reportData.specification),
    };
  }
  const selected = reportData.reports.filter((r) => !r.notForOutput)[
    selectedOutputIndex
  ];
  if (!selected) return null;
  return {
    ...reportData,
    reports: [
      ...reportData.reports.filter((r) => r.notForOutput),
      {
        ...selected,
        specification: buildInputFilterSummarySpec(selected.specification),
      },
    ],
  };
}

/** Returns the set of display columns with an output or input filter. */
export function buildFilteredColumnSet(outputFilterMaps, inputFilterMaps) {
  const result = new Set();
  [outputFilterMaps, inputFilterMaps].forEach((maps) => {
    FILTER_MAP_KEYS.forEach((key) => {
      Object.entries(maps?.[key] ?? {}).forEach(([column, value]) => {
        if (value) result.add(filterDisplayColumn(column));
      });
    });
  });
  return result;
}

/** Returns whether the values for a filter panel are still to arrive. */
export function filterValuesPending({
  usesInputSummary,
  inputLoading,
  inputSettled,
  inputFailed,
  summaryLoading,
  rerunning,
  summary,
}) {
  if (usesInputSummary) {
    return inputLoading || (!inputSettled && !inputFailed);
  }
  return summaryLoading || (rerunning && !summary);
}

/** Returns the target column, filter type, summary, type flags and value multiplier for a filter of a kind on a column. */
export function resolveFilterSource({
  column,
  kind,
  groupingActive,
  rangeGroupedColumns,
  columnsData,
  summaryResults,
  inputSummary,
}) {
  const targetColumn = filterTargetColumn(column, kind, rangeGroupedColumns);
  if (targetColumn !== column) {
    return {
      targetColumn,
      filterType: "value",
      summary: summaryResults?.[targetColumn] ?? null,
      isBinary: false,
      isEnum: false,
      usesInputSummary: false,
      valueMultiplier: 1,
      valueSubType: null,
    };
  }
  const usesInputSummary = kind === FILTER_KIND_INPUT && groupingActive;
  const colDef = columnsData?.find((c) => c.column === column);
  const isInputKind = kind === FILTER_KIND_INPUT;
  const type = isInputKind ? colDef?._inputType : colDef?._type;
  const subType = isInputKind ? colDef?._inputSubType : colDef?._subType;
  const dataType = isInputKind ? colDef?._inputDataType : colDef?._dataType;
  return {
    targetColumn,
    filterType: colDef ? getFilterTypeFromResultColumn(type, subType) : "value",
    summary: usesInputSummary
      ? inputSummary?.[column] ?? null
      : summaryResults?.[column] ?? null,
    isBinary: dataType === BINARY_DATA_TYPE,
    isEnum: type === ENUM_COLUMN_TYPE,
    usesInputSummary,
    valueMultiplier: filterValueMultiplier(subType),
    valueSubType: subType ?? null,
  };
}

/** Returns the sub-type of a column for a filter kind, or null. */
export function columnFilterSubType(columnsData, column, kind) {
  const colDef = columnsData?.find((c) => c.column === column);
  return (
    (kind === FILTER_KIND_INPUT ? colDef?._inputSubType : colDef?._subType) ??
    null
  );
}

/** Returns the filter value multiplier of a column for a filter kind. */
export function columnFilterValueMultiplier(columnsData, column, kind) {
  return filterValueMultiplier(columnFilterSubType(columnsData, column, kind));
}

/** Returns the number formatter for a column's filter values of a filter kind, scaled and marked for its sub-type. */
export function columnFilterNumberFormatter(
  columnsData,
  column,
  kind,
  dataType
) {
  const subType = columnFilterSubType(columnsData, column, kind);
  return createNumberFormatter(
    dataType,
    filterValueMultiplier(subType),
    subTypeValueAffixes(subType)
  );
}

const INPUT_FILTER_CHANGE_TYPES = [
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
];

/** Returns the filter kind a change type belongs to. */
export const filterKindForChangeType = (type) =>
  INPUT_FILTER_CHANGE_TYPES.includes(type)
    ? FILTER_KIND_INPUT
    : FILTER_KIND_OUTPUT;

/** Returns the specs of the non-reverted filter wrappers. */
export const activeFilterSpecs = (wrappers) =>
  (wrappers ?? []).filter((f) => f._status !== "reverted").map((f) => f._spec);

/** Maps summary result columns to their types, keyed by internal name. */
export const summaryColumnTypes = (columns) =>
  Object.fromEntries(
    (columns ?? []).map((col) => [
      col.internalName,
      {
        type: col.type?.type ?? null,
        subType: col.type?.subType ?? null,
        enumerations: col.type?.enumerations ?? null,
      },
    ])
  );

/** Returns a column's active filter chips keyed by filter kind. */
export const chipsByFilterKind = (activeFilterGroups, column) => {
  const group = (activeFilterGroups ?? []).find((g) => g.column === column);
  return Object.fromEntries(
    (group?.sections ?? []).map((section) => [section.kind, section.chips])
  );
};
